import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import ChatLoader from 'src/components/loading/chat-loader.component';
import SetupHeaderCard from 'src/components/setup/setup-header-card.component';
import StatusSwitch from 'src/components/switch/status-switch.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const defaultFaqItem = {
  id: 0,
  question: '',
  answer: '',
  sort_order: 0,
};

const FAQ_PAGE_LIMIT = 20;

const escapeFaqHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizeFaqLinkUrl = (value = '') => {
  const url = String(value || '').trim();
  if (!url) return '';
  if (/^(https?:\/\/|mailto:|tel:|#)/i.test(url)) return url;
  return `https://${url}`;
};

const sanitizeFaqHtml = (html = '') =>
  String(html)
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>.*?<\s*\/\s*\1\s*>/gis, '')
    .replace(/<\s*\/?\s*(script|style|iframe|object|embed)[^>]*>/gis, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/href\s*=\s*("|')\s*javascript:[^"']*\1/gi, 'href="#"');

const buildFaqItemFromForm = (form, responseItem, fallbackSortOrder) => ({
  id: responseItem?.id || form.id,
  question: responseItem?.question ?? form.question,
  answer: responseItem?.answer ?? form.answer,
  sort_order: responseItem?.sort_order ?? form.sort_order ?? fallbackSortOrder,
});

const FAQListItem = memo(function FAQListItem({ item, index, isDragDisabled, isDisabled, onEdit, onDelete, translate }) {
  return (
    <Draggable key={item.id} draggableId={`faq-${item.id}`} index={index} isDragDisabled={isDragDisabled}>
      {(draggableProvided, snapshot) => (
        <Paper
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
          variant="outlined"
          sx={{
            p: 1.5,
            flexShrink: 0,
            bgcolor: 'background.paper',
            boxShadow: snapshot.isDragging ? 4 : 0,
          }}
          style={draggableProvided.draggableProps.style}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Tooltip title={translate('faq_setup_page.drag_to_reorder', { defaultValue: 'Drag to reorder' })}>
              <span>
                <IconButton
                  size="small"
                  disabled={isDragDisabled}
                  sx={{ mt: 0.25, cursor: isDragDisabled ? 'default' : 'grab' }}
                  {...draggableProvided.dragHandleProps}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">{item.question}</Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: 14,
                    color: 'text.secondary',
                    '& p': { my: 0.5 },
                    '& ul, & ol': { pl: 2, my: 0.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeFaqHtml(item.answer) }}
                />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Divider orientation="vertical" flexItem />
                <IconButton size="small" disabled={isDisabled} onClick={() => onEdit(item)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" disabled={isDisabled} onClick={() => onDelete(item)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Draggable>
  );
});

const FAQForm = memo(
  forwardRef(function FAQForm({ onSaved, isDisabled }, ref) {
    const { t: translate } = useTranslation();
    const [saveLoader, setSaveLoader] = useState(false);
    const [itemForm, setItemForm] = useState(defaultFaqItem);
    const [answerMode, setAnswerMode] = useState('text');
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('https://');
    const answerInputRef = useRef(null);
    const answerSelectionRef = useRef(null);
    const { showToast } = useToaster();
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;

    const resetForm = useCallback(() => {
      setItemForm(defaultFaqItem);
    }, []);

    const editItem = useCallback((item) => {
      setItemForm({
        id: item.id,
        question: item.question || '',
        answer: item.answer || '',
        sort_order: item.sort_order || 0,
      });
    }, []);

    const handleAnswerModeChange = (_event, nextMode) => {
      if (!nextMode || isDisabled) return;
      syncAnswerFromEditor();
      setAnswerMode(nextMode);
    };

    useImperativeHandle(
      ref,
      () => ({
        editItem,
        resetForm,
        getEditingItemId: () => itemForm.id,
      }),
      [editItem, itemForm.id, resetForm]
    );

    useEffect(() => {
      const editor = answerInputRef.current;
      if (!editor || document.activeElement === editor) {
        return;
      }
      const nextHtml = sanitizeFaqHtml(itemForm.answer || '');
      if (editor.innerHTML !== nextHtml) {
        editor.innerHTML = nextHtml;
      }
    }, [itemForm.id, itemForm.answer]);

    const syncAnswerFromEditor = () => {
      const editor = answerInputRef.current;
      const nextHtml = sanitizeFaqHtml(editor?.innerHTML || '').trim();
      setItemForm((prev) => (prev.answer === nextHtml ? prev : { ...prev, answer: nextHtml }));
    };

    const runAnswerCommand = (command, value = null) => {
      if (isDisabled) return;
      const editor = answerInputRef.current;
      if (!editor) return;
      editor.focus();
      document.execCommand(command, false, value);
      syncAnswerFromEditor();
    };

    const saveCurrentAnswerSelection = () => {
      const editor = answerInputRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) {
        answerSelectionRef.current = null;
        return;
      }

      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        answerSelectionRef.current = null;
        return;
      }

      answerSelectionRef.current = range.cloneRange();
    };

    const restoreAnswerSelection = () => {
      const editor = answerInputRef.current;
      const selection = window.getSelection();
      const range = answerSelectionRef.current;
      if (!editor || !selection || !range) return false;

      editor.focus();
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    };

    const insertAnswerLink = () => {
      if (isDisabled) return;
      saveCurrentAnswerSelection();
      setLinkUrl('https://');
      setLinkDialogOpen(true);
    };

    const closeLinkDialog = () => {
      setLinkDialogOpen(false);
      setLinkUrl('https://');
      answerSelectionRef.current = null;
    };

    const applyAnswerLink = () => {
      if (isDisabled) return;
      const normalizedUrl = normalizeFaqLinkUrl(linkUrl);
      if (!normalizedUrl) {
        showToastRef.current(
          translate('faq_setup_page.link_url_required', { defaultValue: 'Link URL is required' }),
          'e'
        );
        return;
      }

      const editor = answerInputRef.current;
      if (!editor) return;
      const restored = restoreAnswerSelection();
      const selection = window.getSelection();
      const hasSelectedText = restored && selection && !selection.isCollapsed;

      if (hasSelectedText) {
        document.execCommand('createLink', false, normalizedUrl);
      } else {
        editor.focus();
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${escapeFaqHtml(normalizedUrl)}" target="_blank" rel="noopener noreferrer">${escapeFaqHtml(normalizedUrl)}</a>`
        );
      }

      syncAnswerFromEditor();
      closeLinkDialog();
    };

    const saveItem = () => {
      if (isDisabled) return;
      if (!itemForm.question.trim() || !itemForm.answer.trim()) {
        showToastRef.current(
          translate('faq_setup_page.question_answer_required', {
            defaultValue: 'Question and answer are required',
          }),
          'e'
        );
        return;
      }
      setSaveLoader(true);
      HttpServices.Post({
        url: itemForm.id ? `${userModuleURL.faqItems}/${itemForm.id}` : userModuleURL.faqItems,
        body: {
          id: itemForm.id || undefined,
          question: itemForm.question,
          answer: itemForm.answer,
          sort_order: itemForm.sort_order,
        },
        successCallback: (data, message) => {
          onSaved(itemForm, data);
          resetForm();
          setSaveLoader(false);
          message && showToastRef.current(message, 's');
        },
        failureCallback: (message) => {
          setSaveLoader(false);
          message && showToastRef.current(message, 'e');
        },
      });
    };

    return (
      <>
        <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 16 }}>
          <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            {itemForm.id
              ? translate('faq_setup_page.edit_faq', { defaultValue: 'Edit FAQ' })
              : translate('faq_setup_page.add_faq', { defaultValue: 'Add FAQ' })}
          </Typography>
          <TextField
            fullWidth
            disabled={isDisabled}
            label={translate('faq_setup_page.question', { defaultValue: 'Question' })}
            value={itemForm.question}
            onChange={(e) => setItemForm((prev) => ({ ...prev, question: e.target.value }))}
          />
          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 0.75 }}
            >
              <Typography variant="caption" color="text.secondary">
                {translate('faq_setup_page.answer', { defaultValue: 'Answer' })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={answerMode}
                disabled={isDisabled}
                onChange={handleAnswerModeChange}
              >
                <ToggleButton value="text" aria-label={translate('faq_setup_page.text_answer', { defaultValue: 'Text answer' })}>
                  <TextFieldsIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="html" aria-label={translate('faq_setup_page.html_answer', { defaultValue: 'HTML content' })}>
                  <CodeIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {answerMode === 'text' ? (
              <Box
                ref={answerInputRef}
                contentEditable={!isDisabled}
                suppressContentEditableWarning
                role="textbox"
                aria-label={translate('faq_setup_page.answer', { defaultValue: 'Answer' })}
                data-placeholder={translate('faq_setup_page.answer_placeholder', {
                  defaultValue: 'Type the FAQ answer...',
                })}
                onInput={syncAnswerFromEditor}
                onBlur={syncAnswerFromEditor}
                sx={{
                  minHeight: 150,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  outline: 'none',
                  fontSize: 14,
                  bgcolor: 'background.paper',
                  opacity: isDisabled ? 0.55 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  '&:focus': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                  },
                  '&:empty::before': {
                    content: 'attr(data-placeholder)',
                    color: 'text.disabled',
                    pointerEvents: 'none',
                  },
                  '& p': { my: 0.5 },
                  '& ul, & ol': { pl: 2.5, my: 0.5 },
                }}
              />
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={7}
                disabled={isDisabled}
                value={itemForm.answer}
                placeholder="<p>Type or paste HTML content...</p>"
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, answer: sanitizeFaqHtml(event.target.value) }))
                }
              />
            )}
          </Box>
          {answerMode === 'text' && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Tooltip title={translate('faq_setup_page.bold', { defaultValue: 'Bold' })}>
                <IconButton size="small" disabled={isDisabled} onClick={() => runAnswerCommand('bold')}>
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('faq_setup_page.italic', { defaultValue: 'Italic' })}>
                <IconButton size="small" disabled={isDisabled} onClick={() => runAnswerCommand('italic')}>
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('faq_setup_page.underline', { defaultValue: 'Underline' })}>
                <IconButton size="small" disabled={isDisabled} onClick={() => runAnswerCommand('underline')}>
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('faq_setup_page.bullet_list', { defaultValue: 'Bullet list' })}>
                <IconButton size="small" disabled={isDisabled} onClick={() => runAnswerCommand('insertUnorderedList')}>
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('faq_setup_page.number_list', { defaultValue: 'Numbered list' })}>
                <IconButton size="small" disabled={isDisabled} onClick={() => runAnswerCommand('insertOrderedList')}>
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('faq_setup_page.link', { defaultValue: 'Link' })}>
                <IconButton size="small" disabled={isDisabled} onClick={insertAnswerLink}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">
              {translate('faq_setup_page.preview', { defaultValue: 'Preview' })}
            </Typography>
            <Box
              sx={{
                mt: 1,
                fontSize: 14,
                color: 'text.primary',
                '& p': { my: 0.5 },
                '& ul, & ol': { pl: 2, my: 0.5 },
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizeFaqHtml(
                  itemForm.answer ||
                    translate('faq_setup_page.preview_empty', {
                      defaultValue: 'Your formatted answer preview appears here.',
                    })
                ),
              }}
            />
          </Paper>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {itemForm.id > 0 && (
              <Button variant="text" disabled={isDisabled} onClick={resetForm}>
                {translate('faq_setup_page.cancel', { defaultValue: 'Cancel' })}
              </Button>
            )}
            <LoadingButton variant="contained" disabled={isDisabled} loading={saveLoader} onClick={saveItem}>
              {itemForm.id
                ? translate('faq_setup_page.update_faq', { defaultValue: 'Update FAQ' })
                : translate('faq_setup_page.save_faq', { defaultValue: 'Save FAQ' })}
            </LoadingButton>
          </Stack>
          </Stack>
        </Paper>
        <Dialog open={linkDialogOpen} onClose={closeLinkDialog} fullWidth maxWidth="xs">
          <DialogTitle>
            {translate('faq_setup_page.link_url_prompt', { defaultValue: 'Enter link URL' })}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              autoFocus
              margin="dense"
              label={translate('faq_setup_page.link_url', { defaultValue: 'Link URL' })}
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyAnswerLink();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLinkDialog}>
              {translate('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button variant="contained" onClick={applyAnswerLink}>
              {translate('common.apply', { defaultValue: 'Apply' })}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  })
);

const FAQItemsList = memo(
  forwardRef(function FAQItemsList({ canManageFAQ, isDisabled, formRef }, ref) {
    const { t: translate } = useTranslation();
    const { showToast } = useToaster();
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;

    const [loader, setLoader] = useState(true);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [hasMoreItems, setHasMoreItems] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const itemsRef = useRef([]);
    const searchRef = useRef('');
    const pageRef = useRef(1);
    const hasMoreItemsRef = useRef(true);
    const isLoadingMoreRef = useRef(false);
    const faqFetchRequestRef = useRef(0);
    const reorderInFlightRef = useRef(false);

    useEffect(() => {
      itemsRef.current = items;
    }, [items]);

    useEffect(() => {
      searchRef.current = search;
    }, [search]);

    useEffect(() => {
      pageRef.current = page;
    }, [page]);

    useEffect(() => {
      hasMoreItemsRef.current = hasMoreItems;
    }, [hasMoreItems]);

    useEffect(() => {
      isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    // Stable fetch — no showToast in deps (prevents re-fetch flicker)
    const fetchFAQData = useCallback(
      (searchText = '', nextPage = 1, append = false) => {
        if (!canManageFAQ) {
          setLoader(false);
          return;
        }
        if (append && (isLoadingMoreRef.current || !hasMoreItemsRef.current)) {
          return;
        }

        const requestId = faqFetchRequestRef.current + 1;
        faqFetchRequestRef.current = requestId;

        if (append) {
          setIsLoadingMore(true);
          isLoadingMoreRef.current = true;
        } else {
          setLoader(true);
          setHasMoreItems(true);
          hasMoreItemsRef.current = true;
        }

        const params = new URLSearchParams({
          page: String(nextPage),
          limit: String(FAQ_PAGE_LIMIT),
        });
        if (searchText.trim()) {
          params.set('q', searchText.trim());
        }

        HttpServices.Get({
          url: `${userModuleURL.faqItems}?${params.toString()}`,
          successCallback: (itemData) => {
            if (requestId !== faqFetchRequestRef.current) return;
            const nextItems = Array.isArray(itemData) ? itemData : [];
            setItems((prevItems) => (append ? [...prevItems, ...nextItems] : nextItems));
            setPage(nextPage);
            pageRef.current = nextPage;
            setHasMoreItems(nextItems.length === FAQ_PAGE_LIMIT);
            hasMoreItemsRef.current = nextItems.length === FAQ_PAGE_LIMIT;
            setLoader(false);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
          },
          failureCallback: (message) => {
            if (requestId !== faqFetchRequestRef.current) return;
            setLoader(false);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            message && showToastRef.current(message, 'e');
          },
        });
      },
      [canManageFAQ]
    );

    useEffect(() => {
      const debounceTimer = setTimeout(() => {
        fetchFAQData(searchRef.current, 1, false);
      }, 350);
      return () => clearTimeout(debounceTimer);
    }, [fetchFAQData, search]);

    useImperativeHandle(
      ref,
      () => ({
        upsertSavedItem: (form, data) => {
          const responseItem = data && !Array.isArray(data) && typeof data === 'object' ? data : null;
          const savedItem = buildFaqItemFromForm(form, responseItem, itemsRef.current.length + 1);

          if (form.id) {
            setItems((prevItems) =>
              prevItems.map((item) => (item.id === form.id ? { ...item, ...savedItem } : item))
            );
          } else if (savedItem.id && !searchRef.current.trim()) {
            setItems((prevItems) => [...prevItems, savedItem]);
          } else if (!savedItem.id) {
            fetchFAQData(searchRef.current, 1, false);
          }
        },
      }),
      [fetchFAQData]
    );

    const saveFAQOrder = (nextItems) => {
      const previousItems = itemsRef.current;
      setItems(nextItems);
      reorderInFlightRef.current = true;
      HttpServices.Post({
        url: `${userModuleURL.faqItems}-order`,
        body: { item_ids: nextItems.map((item) => item.id) },
        successCallback: () => {
          reorderInFlightRef.current = false;
        },
        failureCallback: (message) => {
          reorderInFlightRef.current = false;
          setItems(previousItems);
          showToastRef.current(
            message ||
              translate('faq_setup_page.reorder_failed', {
                defaultValue: 'Failed to update FAQ order',
              }),
            'e'
          );
        },
      });
    };

    const handleDragEnd = (result) => {
      if (isDisabled) return;
      if (searchRef.current.trim() || reorderInFlightRef.current) return;
      if (!result.destination || result.destination.index === result.source.index) return;

      const nextItems = [...itemsRef.current];
      const [draggedItem] = nextItems.splice(result.source.index, 1);
      nextItems.splice(result.destination.index, 0, draggedItem);
      saveFAQOrder(nextItems);
    };

    const handleListScroll = (event) => {
      if (loader || isLoadingMoreRef.current || !hasMoreItemsRef.current) return;
      const element = event.currentTarget;
      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distanceFromBottom <= 96) {
        fetchFAQData(searchRef.current, pageRef.current + 1, true);
      }
    };

    const confirmDeleteItem = useCallback(() => {
      if (!deleteTarget || isDisabled) return;
      setDeleteLoader(true);
        HttpServices.Post({
        url: `${userModuleURL.faqItems}/${deleteTarget.id}/delete`,
          body: {},
          successCallback: (_data, message) => {
          if (formRef.current?.getEditingItemId() === deleteTarget.id) {
              formRef.current.resetForm();
            }
          setItems((prevItems) => prevItems.filter((prevItem) => prevItem.id !== deleteTarget.id));
          setDeleteLoader(false);
          setDeleteTarget(null);
            message && showToastRef.current(message, 's');
          },
        failureCallback: (message) => {
          setDeleteLoader(false);
          message && showToastRef.current(message, 'e');
        },
        });
    }, [deleteTarget, formRef, isDisabled]);

    const deleteItem = useCallback(
      (item) => {
        if (isDisabled) return;
        setDeleteTarget(item);
      },
      [isDisabled]
    );

    const editItem = useCallback(
      (item) => {
        if (isDisabled) return;
        formRef.current?.editItem(item);
      },
      [formRef, isDisabled]
    );

    return (
      <>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            height: { md: 'calc(100vh - 245px)' },
            minHeight: { xs: 420, md: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
            <TextField
              fullWidth
              disabled={isDisabled}
              label={translate('faq_setup_page.search_faq', { defaultValue: 'Search FAQ' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="text"
                disabled={isDisabled}
                onClick={() => {
                  setSearch('');
                }}
              >
                {translate('faq_setup_page.clear', { defaultValue: 'Clear' })}
              </Button>
            </Stack>
            {search.trim() && (
              <Typography variant="caption" color="text.secondary">
                {translate('faq_setup_page.reorder_search_note', {
                  defaultValue: 'Clear search to change FAQ display order.',
                })}
              </Typography>
            )}
            {loader ? (
              <ChatLoader
                minHeight={220}
                label={translate('faq_setup_page.loading', { defaultValue: 'Loading FAQs' })}
                description={translate('faq_setup_page.loading_description', {
                  defaultValue: 'Preparing your frequently asked questions',
                })}
              />
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="faq-items" isDropDisabled={Boolean(search.trim()) || isDisabled}>
                  {(droppableProvided) => (
                    <Stack
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      spacing={1.5}
                      onScroll={handleListScroll}
                      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}
                    >
                      {items.map((item, index) => (
                        <FAQListItem
                          key={item.id}
                          item={item}
                          index={index}
                          isDragDisabled={Boolean(search.trim()) || isDisabled}
                          isDisabled={isDisabled}
                          onEdit={editItem}
                          onDelete={deleteItem}
                          translate={translate}
                        />
                      ))}
                      {droppableProvided.placeholder}
                      {!items.length && (
                        <Typography variant="body2" color="text.secondary">
                          {translate('faq_setup_page.no_items', { defaultValue: 'No FAQ items found.' })}
                        </Typography>
                      )}
                      {isLoadingMore && (
                        <Box sx={{ py: 1 }}>
                          <LinearProgress />
                        </Box>
                      )}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Stack>
        </Paper>
        <ConfirmationDialog
          open={Boolean(deleteTarget)}
          onClose={() => !deleteLoader && setDeleteTarget(null)}
          onConfirm={confirmDeleteItem}
          title={translate('common.confirm_deletion', { defaultValue: 'Confirm deletion' })}
          message={translate('faq_setup_page.delete_confirm', {
            defaultValue: 'Delete this FAQ item?',
          })}
          submitButtonName={translate('common.delete', { defaultValue: 'Delete' })}
          loading={deleteLoader}
        />
      </>
    );
  })
);

export default function FAQSetup() {
  const { t: translate } = useTranslation();
  const formRef = useRef(null);
  const listRef = useRef(null);
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const canManageFAQ = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.FAQ_MANAGE
  );
  const [faqSettings, setFaqSettings] = useState({ is_enabled: false });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const toggleInFlightRef = useRef(false);
  const isFAQDisabled = !faqSettings.is_enabled;

  const handleSaved = useCallback((form, data) => {
    listRef.current?.upsertSavedItem(form, data);
  }, []);

  // Stable settings fetch — no showToast in deps
  const fetchFAQSettings = useCallback(() => {
    if (!canManageFAQ) return;

    HttpServices.Get({
      url: userModuleURL.faqSettings,
      successCallback: (data) => {
        setFaqSettings({ is_enabled: data?.is_enabled !== false });
      },
      failureCallback: (message) => {
        message && showToastRef.current(message, 'e');
      },
    });
  }, [canManageFAQ]);

  useEffect(() => {
    fetchFAQSettings();
  }, [fetchFAQSettings]);

  const toggleFAQSettings = useCallback((event) => {
    if (toggleInFlightRef.current) return;

    const nextEnabled = event.target.checked;
    let previousEnabled = true;

    // Optimistic update — never disable the switch (avoids flicker)
    setFaqSettings((prev) => {
      previousEnabled = Boolean(prev.is_enabled);
      return prev.is_enabled === nextEnabled ? prev : { ...prev, is_enabled: nextEnabled };
    });

    toggleInFlightRef.current = true;
    setIsSavingSettings(true);

    HttpServices.Post({
      url: userModuleURL.faqSettings,
      body: { is_enabled: nextEnabled },
      successCallback: (data, message) => {
        const enabled = data?.is_enabled !== false;
        setFaqSettings((prev) =>
          prev.is_enabled === enabled ? prev : { ...prev, is_enabled: enabled }
        );
        toggleInFlightRef.current = false;
        setIsSavingSettings(false);
        message && showToastRef.current(message, 's');
      },
      failureCallback: (message) => {
        setFaqSettings((prev) =>
          prev.is_enabled === previousEnabled ? prev : { ...prev, is_enabled: previousEnabled }
        );
        toggleInFlightRef.current = false;
        setIsSavingSettings(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>{translate('faq_setup_page.title', { defaultValue: 'FAQ Setup' })}</title>
      </Helmet>
      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <SetupHeaderCard
            icon={<QuestionAnswerRoundedIcon />}
            title={translate('faq_setup_page.title', { defaultValue: 'FAQ Setup' })}
            subtitle={translate('faq_setup_page.subtitle', {
              defaultValue: 'Add simple question and answer pairs for customer self-service.',
            })}
            action={
              canManageFAQ ? (
                <StatusSwitch
                  checked={Boolean(faqSettings.is_enabled)}
                  onChange={toggleFAQSettings}
                  // Do NOT use disabled while saving — that causes the flicker
                  inputProps={{
                    'aria-label': faqSettings.is_enabled
                      ? translate('faq_setup_page.disable_faq_widget', {
                          defaultValue: 'Disable FAQ in widget',
                        })
                      : translate('faq_setup_page.enable_faq_widget', {
                          defaultValue: 'Enable FAQ in widget',
                        }),
                  }}
                  switchSx={{
                    opacity: isSavingSettings ? 0.7 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                />
              ) : null
            }
          />

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flexShrink: 0 }}>
            <Stack spacing={2.5}>
            {!canManageFAQ ? (
              <Typography variant="body2" color="text.secondary">
                {translate('faq_setup_page.permission_note', {
                  defaultValue: 'FAQ setup is limited to users with FAQ manage access.',
                })}
              </Typography>
            ) : (
              <Grid
                container
                spacing={2}
                sx={{
                  opacity: isFAQDisabled ? 0.68 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <Grid item xs={12} md={5}>
                  <FAQForm ref={formRef} onSaved={handleSaved} isDisabled={isFAQDisabled} />
                </Grid>
                <Grid item xs={12} md={7} sx={{ minHeight: 0 }}>
                  <FAQItemsList
                    ref={listRef}
                    canManageFAQ={canManageFAQ}
                    isDisabled={isFAQDisabled}
                    formRef={formRef}
                  />
                </Grid>
              </Grid>
            )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
}
